import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';

/**
 * Hook pro resize logiku tabulky tažením za horní okraj.
 * Vrací aktuální výšku a handler pro mouseDown na resize handle.
 */
export const useTableResize = (containerSelector: string, initialHeight?: number) => {
    const [tableHeight, setTableHeight] = useState(() => initialHeight ?? Math.round(window.innerHeight / 2));
    const tableHeightRef = useRef(tableHeight);
    tableHeightRef.current = tableHeight;

    // Po každém renderu naměří kde začíná block-body a nastaví mu výšku do spodku stránky.
    // react-virtualized potřebuje explicitní px výšku na block-body.
    // Zároveň vystavíme aktuální výšku tabulky přes CSS proměnnou --att-table-height, aby na ni
    // mohl reagovat otevřený sidebar s detailem objektu (ukotvený nahoře, aby nezajížděl pod tabulku).
    useLayoutEffect(() => {
        document.documentElement.style.setProperty('--att-table-height', `${(tableHeight - 32)}px`);
        const blockBody = document.querySelector(`${containerSelector} .ui-v4-block-body`) as HTMLElement;
        if (!blockBody) return;
        const top = blockBody.getBoundingClientRect().top;
        blockBody.style.height = `${Math.max(80, window.innerHeight - top)}px`;
    });

    // Po zavření tabulky proměnnou zrušíme, ať sidebar zase využije celou výšku (fallback 0px).
    useEffect(
        () => () => {
            document.documentElement.style.removeProperty('--att-table-height');
        },
        [],
    );

    const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if (e.button !== 0) return;
        e.preventDefault();

        const startY = e.clientY;
        const startHeight = tableHeightRef.current;

        const iframes = document.querySelectorAll<HTMLIFrameElement>('iframe');
        iframes.forEach(f => (f.style.pointerEvents = 'none'));
        document.body.style.cursor = 'n-resize';
        document.body.style.userSelect = 'none';

        const onMove = (ev: MouseEvent) => {
            const deltaY = startY - ev.clientY;
            setTableHeight(Math.max(150, Math.min(window.innerHeight - 100, startHeight + deltaY)));
        };

        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            iframes.forEach(f => (f.style.pointerEvents = ''));
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }, []);

    return {tableHeight, handleMouseDown};
};
